import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
  Trash2, BookmarkPlus, Info, ArrowRight, ExternalLink, Plus, Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import {
  PageHeader, TidakPunyaAkses, Field, Modal, inputCls, selectCls, ADS_PLATFORMS, PLATFORM_LABEL,
  useCampaignMap, namaCampaign,
} from "./_shared";

/**
 * Impor CSV Ads Manager (Meta/TikTok) — Fase 3 (brief butir 2-4). Mengonsumsi
 * `backend/routers/marketing/ads_import.py`.
 *
 * Router `ads_import.py` SUDAH terdaftar di `routers/marketing/__init__.py` dan
 * sudah live di produksi sejak 11 Agu 2026 — komentar lama di sini yang bilang
 * "belum didaftarkan, endpoint balas 404" sudah tidak berlaku.
 *
 * §11 KEPUTUSAN-TERTUNDA (selesai 21 Agu 2026): editor template mandiri —
 * lihat/ubah/buat template pemetaan TANPA unggah CSV. Sebelumnya menyimpan
 * atau mengubah template hanya bisa lewat efek samping commit import
 * (`save_as_template_nama`), jadi mengubah satu kolom pemetaan mengharuskan
 * mengunggah CSV lagi. `TabelPemetaan` di bawah dipakai DUA kali (langkah 2
 * pratinjau CSV, dan modal editor template) — satu sumber render, bukan grid
 * kembar (CLAUDE.md: "kalau butuh sesuatu di dua tempat, jadikan satu sumber").
 */
export default function AdsImport() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_ads", "view");
  const bolehImport = can("marketing_ads", "create");
  const bolehTimpa = can("marketing_ads", "update");
  const bolehHapusTemplate = can("marketing_ads", "delete");
  const bolehLihatCampaign = can("marketing_campaign", "view");
  const { campaignMap } = useCampaignMap(bolehLihatCampaign);

  const [platform, setPlatform] = useState(ADS_PLATFORMS[0]);
  const [file, setFile] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [mapping, setMapping] = useState({}); // { namaKolomCsv: fieldKeyTujuan }
  const [mappingDirty, setMappingDirty] = useState(false);
  const [busyPreview, setBusyPreview] = useState(false);
  const [busyCommit, setBusyCommit] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [simpanTemplate, setSimpanTemplate] = useState(false);
  const [namaTemplateBaru, setNamaTemplateBaru] = useState("");
  const [commitResult, setCommitResult] = useState(null);

  const { data: templatesData, refetch: refetchTemplates } = useDataQuery(
    ["marketing", "ads-import-templates", platform],
    () => api.get("/marketing/ads-import/templates", { params: { platform } }).then((r) => r.data.items),
    { enabled: bolehLihat },
  );
  const templates = templatesData || [];

  // Katalog field tujuan — dulu tidak pernah dipanggil frontend (§11).
  // Dibutuhkan editor template karena tanpa CSV tidak ada `previewResult.
  // target_fields` untuk mengisi pilihan "Dipetakan ke Field".
  const { data: fieldsData } = useDataQuery(
    ["marketing", "ads-import-fields"],
    () => api.get("/marketing/ads-import/fields").then((r) => r.data),
    { enabled: bolehLihat },
  );
  const fieldCatalog = fieldsData?.fields || [];
  const platformCatalog = fieldsData?.platforms || ADS_PLATFORMS;

  // ===== Editor template mandiri (buat/ubah tanpa unggah CSV) =====
  // Bentuk baris ARRAY (bukan objek mapping langsung) supaya nama kolom bisa
  // diketik bebas (termasuk sementara kosong/duplikat saat mengetik) tanpa
  // kehilangan identitas baris — beda dengan mapping CSV yang kuncinya sudah
  // pasti (nama kolom asli file), di sini user MENGARANG nama kolom.
  const [templateEditor, setTemplateEditor] = useState(null); // {id, nama, platform, rows:[{key,kolom,field}], busy}
  const rowKeyRef = useRef(0);
  const barisBaru = (kolom = "", field = "") => ({ key: `r${rowKeyRef.current++}`, kolom, field });

  const bukaTemplateBaru = () => {
    setTemplateEditor({ id: null, nama: "", platform, rows: [barisBaru(), barisBaru()], busy: false });
  };
  const bukaUbahTemplate = (tpl) => {
    const rows = Object.entries(tpl.mapping || {}).map(([kolom, field]) => barisBaru(kolom, field));
    setTemplateEditor({ id: tpl.id, nama: tpl.nama, platform: tpl.platform, rows: rows.length ? rows : [barisBaru()], busy: false });
  };
  const tutupTemplateEditor = () => setTemplateEditor(null);

  const editorFieldBentrok = useMemo(() => {
    if (!templateEditor) return new Set();
    const hitung = {};
    templateEditor.rows.forEach((r) => { if (r.field) hitung[r.field] = (hitung[r.field] || 0) + 1; });
    return new Set(Object.entries(hitung).filter(([, n]) => n > 1).map(([f]) => f));
  }, [templateEditor]);

  const ubahBarisKolom = (key, val) => setTemplateEditor((t) => ({ ...t, rows: t.rows.map((r) => (r.key === key ? { ...r, kolom: val } : r)) }));
  const ubahBarisField = (key, val) => setTemplateEditor((t) => ({ ...t, rows: t.rows.map((r) => (r.key === key ? { ...r, field: val } : r)) }));
  const hapusBarisEditor = (key) => setTemplateEditor((t) => ({ ...t, rows: t.rows.filter((r) => r.key !== key) }));
  const tambahBarisEditor = () => setTemplateEditor((t) => ({ ...t, rows: [...t.rows, barisBaru()] }));

  const simpanTemplateEditor = async () => {
    if (!templateEditor) return;
    const nama = templateEditor.nama.trim();
    if (!nama) { toast.error("Nama template wajib diisi"); return; }
    if (editorFieldBentrok.size > 0) { toast.error("Satu field tujuan tidak boleh dipetakan dari lebih dari satu baris — perbaiki dulu."); return; }
    const kolomTerpakai = new Set();
    let adaKolomGanda = false;
    const mappingBaru = {};
    templateEditor.rows.forEach((r) => {
      const kolom = r.kolom.trim();
      if (!kolom || !r.field) return;
      const kolomKey = kolom.toLowerCase();
      if (kolomTerpakai.has(kolomKey)) adaKolomGanda = true;
      kolomTerpakai.add(kolomKey);
      mappingBaru[kolom] = r.field;
    });
    if (adaKolomGanda) { toast.error("Ada nama kolom yang sama dipakai di lebih dari satu baris — ganti salah satunya."); return; }
    if (Object.keys(mappingBaru).length === 0) { toast.error("Petakan minimal satu kolom sebelum menyimpan."); return; }
    setTemplateEditor((t) => ({ ...t, busy: true }));
    try {
      if (templateEditor.id) {
        await api.patch(`/marketing/ads-import/templates/${templateEditor.id}`, { nama, platform: templateEditor.platform, mapping: mappingBaru });
        toast.success("Template diperbarui");
      } else {
        await api.post("/marketing/ads-import/templates", { nama, platform: templateEditor.platform, mapping: mappingBaru });
        toast.success("Template dibuat");
      }
      setTemplateEditor(null);
      refetchTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan template");
      setTemplateEditor((t) => (t ? { ...t, busy: false } : t));
    }
  };

  // Dibungkus useMemo sendiri: `|| []` melahirkan array BARU tiap render, dan
  // array itu jadi dependensi useMemo di bawahnya — tanpa ini, targetFieldMap
  // dihitung ulang setiap render dan memo-nya percuma.
  const targetFields = useMemo(() => previewResult?.target_fields || [], [previewResult]);
  const targetFieldMap = useMemo(() => {
    const m = {};
    targetFields.forEach((f) => { m[f.key] = f; });
    return m;
  }, [targetFields]);

  // Kolom yang dipetakan ke field tujuan yang SAMA (tidak valid, backend
  // menolak) — dideteksi di sisi klien supaya kelihatan SEBELUM submit, bukan
  // sesudah 400 dari server (rule "jangan tampilkan tombol yang ujungnya error").
  const fieldBentrok = useMemo(() => {
    const hitung = {};
    Object.values(mapping).forEach((f) => { if (f) hitung[f] = (hitung[f] || 0) + 1; });
    return new Set(Object.entries(hitung).filter(([, n]) => n > 1).map(([f]) => f));
  }, [mapping]);
  const adaBentrok = fieldBentrok.size > 0;

  const resetSemua = () => {
    setFile(null); setTemplateId(""); setPreviewResult(null); setMapping({});
    setMappingDirty(false); setCommitResult(null); setOverwriteExisting(false);
    setSimpanTemplate(false); setNamaTemplateBaru("");
  };

  const pilihFile = (f) => {
    setFile(f);
    setPreviewResult(null); setMapping({}); setMappingDirty(false); setCommitResult(null);
  };

  const jalankanPreview = async (pakaiMappingSaatIni) => {
    if (!file) { toast.error("Pilih file CSV dulu"); return; }
    setBusyPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (pakaiMappingSaatIni && Object.keys(mapping).length > 0) {
        fd.append("mapping", JSON.stringify(mapping));
      } else if (templateId) {
        fd.append("template_id", templateId);
      }
      const r = await api.post("/marketing/ads-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewResult(r.data);
      setMapping(r.data.suggested_mapping || {});
      setMappingDirty(false);
      setCommitResult(null);
      if ((r.data.missing_required || []).length > 0) {
        toast.error("Masih ada kolom wajib yang belum terpetakan — lengkapi lalu pratinjau ulang.");
      } else {
        toast.success(`Pratinjau siap — ${r.data.total_data_rows} baris data terbaca.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membaca file CSV"); }
    finally { setBusyPreview(false); }
  };

  const ubahMapping = (kolom, fieldKey) => {
    setMapping((m) => {
      const baru = { ...m };
      if (!fieldKey) delete baru[kolom];
      else baru[kolom] = fieldKey;
      return baru;
    });
    setMappingDirty(true);
  };

  const jalankanCommit = async () => {
    if (!file) { toast.error("Pilih file CSV dulu"); return; }
    if (!previewResult || mappingDirty) { toast.error("Pratinjau ulang dulu sebelum impor — pemetaan berubah sejak pratinjau terakhir."); return; }
    if ((previewResult.missing_required || []).length > 0) { toast.error("Masih ada kolom wajib yang belum terpetakan"); return; }
    if (adaBentrok) { toast.error("Ada field tujuan yang dipetakan dari lebih dari satu kolom — perbaiki dulu"); return; }
    setBusyCommit(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("platform", platform);
      fd.append("mapping", JSON.stringify(mapping));
      fd.append("overwrite_existing", overwriteExisting ? "true" : "false");
      if (simpanTemplate && namaTemplateBaru.trim()) fd.append("save_as_template_nama", namaTemplateBaru.trim());
      const r = await api.post("/marketing/ads-import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCommitResult(r.data);
      toast.success(r.data.message || "Impor selesai");
      if (simpanTemplate && namaTemplateBaru.trim()) refetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengimpor CSV"); }
    finally { setBusyCommit(false); }
  };

  const pakaiTemplate = async (tpl) => {
    setTemplateId(tpl.id);
    if (tpl.platform && tpl.platform !== platform) setPlatform(tpl.platform);
    if (file) await jalankanPreviewDenganTemplate(tpl.id);
  };
  // Dipisah dari `jalankanPreview` supaya bisa dipanggil segera setelah
  // `setTemplateId` tanpa menunggu re-render (closure state lama).
  const jalankanPreviewDenganTemplate = async (tplId) => {
    setBusyPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("template_id", tplId);
      const r = await api.post("/marketing/ads-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreviewResult(r.data);
      setMapping(r.data.suggested_mapping || {});
      setMappingDirty(false);
      setCommitResult(null);
      toast.success(`Template diterapkan — ${r.data.total_data_rows} baris data terbaca.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menerapkan template"); }
    finally { setBusyPreview(false); }
  };

  const hapusTemplate = async (tpl) => {
    if (!window.confirm(`Hapus template "${tpl.nama}"?`)) return;
    try {
      await api.delete(`/marketing/ads-import/templates/${tpl.id}`);
      toast.success("Template dihapus");
      if (templateId === tpl.id) setTemplateId("");
      refetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus template"); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={UploadCloud} title="Impor CSV Iklan" />
        <TidakPunyaAkses label="Impor CSV Iklan" testid="mkt-adsimport-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-adsimport">
      <PageHeader
        icon={UploadCloud} title="Impor CSV Iklan"
        desc="Unggah export CSV Meta Ads Manager / TikTok Ads Manager, cocokkan kolomnya, lalu impor massal ke log ads harian."
        actions={<Link to="/marketing/ads" className="text-xs font-medium text-emerald-700 hover:underline inline-flex items-center gap-1">
          Lihat Log Ads <ExternalLink className="w-3 h-3" />
        </Link>}
      />

      {!bolehImport && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-800">
          Anda tidak punya izin mengimpor (<code>marketing_ads.create</code>) — hanya bisa melihat daftar template
          tersimpan di bawah. Hubungi admin untuk meminta akses impor.
        </div>
      )}

      {/* ===== Langkah 1: Platform + File + Template ===== */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="mkt-adsimport-step1">
        <p className="text-sm font-semibold text-slate-900">1. Pilih Platform &amp; Unggah File</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Platform" required>
            <select value={platform} onChange={(e) => { setPlatform(e.target.value); setTemplateId(""); }} data-testid="mkt-adsimport-platform" className={selectCls}>
              {ADS_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
            </select>
          </Field>
          <Field label="File CSV" required hint="Export asli Meta Ads Manager / TikTok Ads Manager (.csv, bukan .xlsx).">
            <input type="file" accept=".csv" disabled={!bolehImport} data-testid="mkt-adsimport-file"
                   onChange={(e) => pilihFile(e.target.files?.[0] || null)}
                   className="w-full text-sm file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs disabled:opacity-50" />
          </Field>
        </div>

        {(templates.length > 0 || bolehImport) && (
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="mkt-adsimport-tpl-panel">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs font-semibold text-slate-700">Template Pemetaan Tersimpan ({PLATFORM_LABEL[platform]})</p>
              {bolehImport && (
                <button type="button" onClick={bukaTemplateBaru} data-testid="mkt-adsimport-tpl-buat"
                        className="h-7 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-medium text-emerald-700 inline-flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Template Baru
                </button>
              )}
            </div>
            {templates.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {templates.map((tpl) => (
                  <div key={tpl.id} className={`inline-flex items-center gap-1 rounded-full border pl-3 pr-1 h-7 text-xs ${templateId === tpl.id ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}>
                    <button type="button" onClick={() => pakaiTemplate(tpl)} disabled={!bolehImport || !file} data-testid={`mkt-adsimport-tpl-pakai-${tpl.id}`}
                            className="font-medium disabled:opacity-50" title={!file ? "Pilih file dulu" : "Pakai template ini"}>
                      {tpl.nama}
                    </button>
                    {bolehTimpa && (
                      <button type="button" onClick={() => bukaUbahTemplate(tpl)} data-testid={`mkt-adsimport-tpl-ubah-${tpl.id}`}
                              className="w-5 h-5 rounded-full hover:bg-slate-200 text-slate-500 grid place-items-center" title="Ubah pemetaan template">
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                    {bolehHapusTemplate && (
                      <button type="button" onClick={() => hapusTemplate(tpl)} data-testid={`mkt-adsimport-tpl-hapus-${tpl.id}`} className="w-5 h-5 rounded-full hover:bg-rose-100 text-rose-500 grid place-items-center">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400" data-testid="mkt-adsimport-tpl-kosong">Belum ada template untuk platform ini.</p>
            )}
          </div>
        )}

        {bolehImport && (
          <div className="flex items-center gap-2">
            <button onClick={() => jalankanPreview(false)} disabled={!file || busyPreview} data-testid="mkt-adsimport-preview-btn"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> {busyPreview ? "Membaca..." : "Pratinjau"}
            </button>
            {(file || previewResult) && (
              <button onClick={resetSemua} className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-600 inline-flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Mulai Ulang
              </button>
            )}
          </div>
        )}
      </div>

      {/* ===== Langkah 2: Pemetaan kolom + pratinjau data ===== */}
      {previewResult && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="mkt-adsimport-step2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold text-slate-900">2. Cocokkan Pemetaan Kolom ({previewResult.total_data_rows} baris data terbaca)</p>
            <button onClick={() => jalankanPreview(true)} disabled={busyPreview} data-testid="mkt-adsimport-preview-ulang"
                    className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-600 inline-flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Pratinjau Ulang
            </button>
          </div>

          {previewResult.missing_required?.length > 0 && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 inline-flex items-start gap-1.5" data-testid="mkt-adsimport-missing">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Kolom wajib belum terpetakan: <b>{previewResult.missing_required.join("; ")}</b> — pilih kolom CSV yang sesuai di tabel bawah.
            </div>
          )}
          {adaBentrok && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              Satu field tujuan tidak boleh dipetakan dari lebih dari satu kolom: {[...fieldBentrok].map((f) => targetFieldMap[f]?.label || f).join(", ")}.
            </div>
          )}
          {mappingDirty && !adaBentrok && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Pemetaan berubah — klik "Pratinjau Ulang" untuk memperbarui contoh hasil sebelum mengimpor.
            </div>
          )}

          <TabelPemetaan
            baris={previewResult.columns.map((kolom) => ({
              key: kolom, kolom,
              contoh: previewResult.sample_rows_raw?.[0]?.[kolom] ?? "",
              field: mapping[kolom] || "",
              testIdField: `mkt-adsimport-map-${kolom.replace(/\W+/g, "-")}`,
            }))}
            targetFields={targetFields}
            fieldBentrok={fieldBentrok}
            disabled={!bolehImport}
            showContoh
            onUbahField={ubahMapping}
          />

          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1.5">Pratinjau 5 Baris Pertama (hasil parse sungguhan)</p>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-left">Baris CSV</th>
                    <th className="px-3 py-2 font-semibold text-left">Hasil</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(previewResult.preview_rows || []).map((pr) => (
                    <tr key={pr.baris}>
                      <td className="px-3 py-1.5 font-mono text-slate-500 align-top">#{pr.baris}</td>
                      <td className="px-3 py-1.5">
                        {pr.error ? (
                          <span className="text-rose-600 inline-flex items-start gap-1"><XCircle className="w-3 h-3 shrink-0 mt-0.5" /> {pr.error}</span>
                        ) : (
                          <span className="text-emerald-700 inline-flex items-start gap-1">
                            <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />
                            {pr.hasil.tanggal} · {bolehLihatCampaign ? namaCampaign(campaignMap, pr.hasil.campaign_id) : pr.hasil.campaign_id.slice(0, 8) + "…"} ·
                            {" "}spend {pr.hasil.spend} · impr {pr.hasil.impressions} · klik {pr.hasil.clicks}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(previewResult.preview_rows || []).length === 0 && (
                    <tr><td colSpan={2} className="px-3 py-4 text-center text-slate-400">Lengkapi pemetaan kolom wajib dulu untuk melihat contoh hasil.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== Langkah 3: opsi commit ===== */}
          {bolehImport && (
            <div className="border-t border-slate-100 pt-3 space-y-3">
              <p className="text-sm font-semibold text-slate-900">3. Impor</p>
              {bolehTimpa ? (
                <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={overwriteExisting} onChange={(e) => setOverwriteExisting(e.target.checked)}
                         data-testid="mkt-adsimport-overwrite" className="mt-0.5" />
                  <span>Timpa data yang sudah ada (baris CSV yang kombinasi campaign/tanggalnya sudah tercatat akan DIPERBARUI, bukan ditolak sebagai duplikat).</span>
                </label>
              ) : (
                <p className="text-[11px] text-slate-400">Untuk menimpa data yang sudah ada, minta izin <code>marketing_ads.update</code> ke admin — baris duplikat akan ditolak (bukan diperbarui) sampai izin itu diberikan.</p>
              )}
              <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox" checked={simpanTemplate} onChange={(e) => setSimpanTemplate(e.target.checked)} data-testid="mkt-adsimport-simpan-tpl-cb" className="mt-0.5" />
                <span>Simpan pemetaan ini sebagai template untuk dipakai lagi nanti.</span>
              </label>
              {simpanTemplate && (
                <input value={namaTemplateBaru} onChange={(e) => setNamaTemplateBaru(e.target.value)} placeholder="Nama template, cth. Meta Ads Harian"
                       data-testid="mkt-adsimport-nama-tpl" className={`${inputCls} h-9 max-w-sm`} />
              )}
              <button onClick={jalankanCommit}
                      disabled={busyCommit || mappingDirty || adaBentrok || (previewResult.missing_required || []).length > 0}
                      data-testid="mkt-adsimport-commit-btn"
                      title={mappingDirty ? "Pratinjau ulang dulu" : undefined}
                      className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2">
                <ArrowRight className="w-4 h-4" /> {busyCommit ? "Mengimpor..." : `Impor ${previewResult.total_data_rows} Baris`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== Hasil commit ===== */}
      {commitResult && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="mkt-adsimport-result">
          <p className="text-sm font-semibold text-slate-900">Hasil Impor</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <RingkasanKartu label="Berhasil" value={commitResult.berhasil} warna="emerald" testid="mkt-adsimport-r-berhasil" />
            <RingkasanKartu label="Ditimpa" value={commitResult.ditimpa} warna="sky" testid="mkt-adsimport-r-ditimpa" />
            <RingkasanKartu label="Duplikat Ditolak" value={commitResult.duplikat_ditolak} warna="amber" testid="mkt-adsimport-r-duplikat" />
            <RingkasanKartu label="Gagal" value={commitResult.gagal} warna="rose" testid="mkt-adsimport-r-gagal" />
          </div>

          <DaftarBarisHasil title="Baris Gagal — betulkan lalu unggah ulang" testid="mkt-adsimport-list-gagal"
                             items={commitResult.detail_gagal} warna="rose"
                             render={(it) => `Baris ${it.baris}: ${it.alasan}`} />
          <DaftarBarisHasil title="Baris Duplikat Ditolak" testid="mkt-adsimport-list-duplikat"
                             items={commitResult.detail_duplikat} warna="amber"
                             render={(it) => `Baris ${(it.baris || []).join(", ")}: ${bolehLihatCampaign ? namaCampaign(campaignMap, it.campaign_id) : it.campaign_id} — ${it.tanggal}${it.alasan ? ` (${it.alasan})` : ""}`} />
          <DaftarBarisHasil title="Baris Ditimpa" testid="mkt-adsimport-list-ditimpa"
                             items={commitResult.detail_ditimpa} warna="sky"
                             render={(it) => `Baris ${(it.baris || []).join(", ")}: ${bolehLihatCampaign ? namaCampaign(campaignMap, it.campaign_id) : it.campaign_id} — ${it.tanggal}`} />
          <DaftarBarisHasil title="Baris Berhasil" testid="mkt-adsimport-list-berhasil"
                             items={commitResult.detail_berhasil} warna="emerald"
                             render={(it) => `Baris ${(it.baris || []).join(", ")}: ${bolehLihatCampaign ? namaCampaign(campaignMap, it.campaign_id) : it.campaign_id} — ${it.tanggal}`} />

          <div className="flex items-center gap-2 pt-1">
            <Link to="/marketing/ads" className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <BookmarkPlus className="w-4 h-4" /> Lihat di Log Ads
            </Link>
            <button onClick={resetSemua} className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-600 inline-flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Impor File Lain
            </button>
          </div>
        </div>
      )}

      {/* ===== Editor template mandiri — buat/ubah TANPA unggah CSV (§11) ===== */}
      <Modal open={!!templateEditor} onClose={tutupTemplateEditor}
             title={templateEditor?.id ? "Ubah Template Pemetaan" : "Buat Template Pemetaan Baru"}
             maxWidth="max-w-2xl" testid="mkt-adsimport-tpl-editor"
             footer={<>
               <button onClick={tutupTemplateEditor} data-testid="mkt-adsimport-tpl-batal"
                       className="h-9 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-600">
                 Batal
               </button>
               <button onClick={simpanTemplateEditor} disabled={templateEditor?.busy || editorFieldBentrok.size > 0}
                       data-testid="mkt-adsimport-tpl-simpan"
                       className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {templateEditor?.busy ? "Menyimpan..." : "Simpan"}
               </button>
             </>}>
        {templateEditor && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Nama Template" required>
                <input value={templateEditor.nama} onChange={(e) => setTemplateEditor((t) => ({ ...t, nama: e.target.value }))}
                       placeholder="cth. Meta Ads Harian" data-testid="mkt-adsimport-tpl-f-nama" className={inputCls} />
              </Field>
              <Field label="Platform" required>
                <select value={templateEditor.platform} onChange={(e) => setTemplateEditor((t) => ({ ...t, platform: e.target.value }))}
                        data-testid="mkt-adsimport-tpl-f-platform" className={selectCls}>
                  {platformCatalog.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p] || p}</option>)}
                </select>
              </Field>
            </div>

            {editorFieldBentrok.size > 0 && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Satu field tujuan tidak boleh dipetakan dari lebih dari satu baris — perbaiki dulu.
              </div>
            )}

            <TabelPemetaan
              baris={templateEditor.rows.map((r) => ({
                key: r.key, kolom: r.kolom, field: r.field,
                testIdKolom: `mkt-adsimport-tpl-kolom-${r.key}`,
                testIdField: `mkt-adsimport-tpl-map-${r.key}`,
                testIdHapus: `mkt-adsimport-tpl-hapus-${r.key}`,
              }))}
              targetFields={fieldCatalog}
              fieldBentrok={editorFieldBentrok}
              editableKolom
              onUbahKolom={ubahBarisKolom}
              onUbahField={ubahBarisField}
              onHapusBaris={hapusBarisEditor}
            />
            <button type="button" onClick={tambahBarisEditor} data-testid="mkt-adsimport-tpl-tambah-baris"
                    className="h-8 px-3 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Tambah Baris
            </button>
            <p className="text-[11px] text-slate-400">
              Isi nama kolom persis seperti yang akan muncul di file CSV nanti (mis. "Amount spent (IDR)"), lalu pilih field tujuannya di aplikasi.
              Kolom wajib: tanggal, dan campaign (nama ATAU id).
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

/** Tabel pemetaan kolom CSV -> field tujuan — dipakai langkah 2 pratinjau
 *  import (kolom read-only, berasal dari file yang diunggah) MAUPUN editor
 *  template mandiri (kolom bisa diketik bebas, tanpa file). SATU render untuk
 *  keduanya supaya tidak ada dua grid yang bisa menyimpang (CLAUDE.md). */
function TabelPemetaan({
  baris, targetFields, fieldBentrok, disabled, showContoh = false, editableKolom = false,
  onUbahKolom, onUbahField, onHapusBaris,
}) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-3 py-2 font-semibold text-left">Kolom di CSV</th>
            {showContoh && <th className="px-3 py-2 font-semibold text-left">Contoh Nilai</th>}
            <th className="px-3 py-2 font-semibold text-left">Dipetakan ke Field</th>
            {editableKolom && <th className="px-3 py-2 w-8" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {baris.map((b) => {
            const bentrok = b.field && fieldBentrok.has(b.field);
            return (
              <tr key={b.key} className={bentrok ? "bg-rose-50/60" : ""}>
                <td className="px-3 py-1.5 font-mono text-slate-700">
                  {editableKolom ? (
                    <input value={b.kolom} onChange={(e) => onUbahKolom(b.key, e.target.value)} disabled={disabled}
                           placeholder="Nama kolom di CSV" data-testid={b.testIdKolom}
                           className={`${inputCls} h-8 text-xs font-mono`} />
                  ) : b.kolom}
                </td>
                {showContoh && <td className="px-3 py-1.5 text-slate-400 truncate max-w-[160px]">{String(b.contoh ?? "")}</td>}
                <td className="px-3 py-1.5">
                  <select value={b.field || ""} onChange={(e) => onUbahField(b.key, e.target.value)} disabled={disabled}
                          data-testid={b.testIdField}
                          className={`${inputCls} h-8 text-xs ${bentrok ? "border-rose-300" : ""}`}>
                    <option value="">— tidak dipetakan —</option>
                    {targetFields.map((f) => <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>)}
                  </select>
                </td>
                {editableKolom && (
                  <td className="px-3 py-1.5">
                    <button type="button" onClick={() => onHapusBaris(b.key)} disabled={disabled}
                            data-testid={b.testIdHapus}
                            className="w-6 h-6 rounded hover:bg-rose-100 text-rose-500 grid place-items-center">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RingkasanKartu({ label, value, warna, testid }) {
  const kelas = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  }[warna];
  return (
    <div className={`rounded-lg border p-3 ${kelas}`} data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-xl font-bold font-mono mt-0.5">{value}</p>
    </div>
  );
}

function DaftarBarisHasil({ title, items, warna, render, testid }) {
  if (!items || items.length === 0) return null;
  const kelasTeks = { rose: "text-rose-700", amber: "text-amber-700", sky: "text-sky-700", emerald: "text-emerald-700" }[warna];
  return (
    <div data-testid={testid}>
      <p className={`text-xs font-semibold mb-1 ${kelasTeks}`}>{title} ({items.length})</p>
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.map((it, i) => (
          <p key={i} className="px-3 py-1.5 text-xs text-slate-600">{render(it)}</p>
        ))}
      </div>
    </div>
  );
}
